import React from 'react'
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Box,
  VStack,
  Text,
  Badge,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  List,
  ListItem,
  ListIcon,
  useColorModeValue,
} from '@chakra-ui/react'
import { MdErrorOutline } from 'react-icons/md'

export function ValidationModal({ isOpen, onClose, errors = [], onAutoResolve }) {
  const bgColor = useColorModeValue('white', '#1E293B')
  const itemBg = useColorModeValue('red.50', 'whiteAlpha.50')
  const itemBorder = useColorModeValue('red.200', 'red.900')
  const itemColor = useColorModeValue('red.800', 'red.200')
  const isValid = errors.length === 0

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" isCentered>
      <ModalOverlay backdropFilter="blur(3px)" />
      <ModalContent bg={bgColor} borderRadius="12px" boxShadow="2xl">
        <ModalHeader display="flex" alignItems="center" gap={2} pb={2}>
          <Text fontWeight="700">Validation Result</Text>
          {isValid ? (
            <Badge colorScheme="green" px={2} py={0.5} borderRadius="full">
              Valid
            </Badge>
          ) : (
            <Badge colorScheme="red" px={2} py={0.5} borderRadius="full">
              {errors.length} Issue{errors.length > 1 ? 's' : ''}
            </Badge>
          )}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py={4}>
          {isValid ? (
            <Alert
              status="success"
              variant="subtle"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              borderRadius="8px"
              py={6}
            >
              <AlertIcon boxSize="40px" mr={0} />
              <AlertTitle mt={4} mb={1} fontSize="lg">
                No issues detected!
              </AlertTitle>
              <AlertDescription maxWidth="sm" color="gray.500" fontSize="sm">
                All seat IDs and row/seat numbering are unique across all zones. The plan is valid and ready for bookings.
              </AlertDescription>
            </Alert>
          ) : (
            <VStack spacing={3} align="stretch">
              <Alert status="error" borderRadius="8px" fontSize="sm">
                <AlertIcon />
                <Box>
                  <Text fontWeight="600">Plan has configuration conflicts</Text>
                  <Text fontSize="xs">
                    The following issues must be resolved before this plan can be safely booked:
                  </Text>
                </Box>
              </Alert>
              <List spacing={2} maxH="320px" overflowY="auto" pr={1}>
                {errors.map((err, idx) => (
                  <ListItem
                    key={idx}
                    p={2.5}
                    borderRadius="6px"
                    bg={itemBg}
                    border="1px solid"
                    borderColor={itemBorder}
                    fontSize="xs"
                    color={itemColor}
                    display="flex"
                    alignItems="flex-start"
                    gap={2}
                  >
                    <ListIcon as={MdErrorOutline} color="red.500" boxSize="18px" mt={0.5} />
                    <Text flex="1">{err}</Text>
                  </ListItem>
                ))}
              </List>
            </VStack>
          )}
        </ModalBody>
        <ModalFooter pt={2} display="flex" justifyContent="space-between">
          {!isValid && onAutoResolve && (
            <Button
              colorScheme="green"
              size="sm"
              borderRadius="6px"
              onClick={() => {
                onAutoResolve()
              }}
            >
              Auto-Resolve Duplicates
            </Button>
          )}
          <Button colorScheme="brand" size="sm" onClick={onClose} borderRadius="6px" ml="auto">
            Close
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
